<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { postLoginWithExisting } from '@/api/endpoints/loginWeb';
import { useActivationStore, useGeneralPurposeModalStore, useLocalizationStore } from '@/store';
import { useSessionStore } from '@/store/sessionStore';
import { handleApiRequest } from '@/api/handleApiRequest';
import { getRegistrationNextStepSkipLoginWithExting } from 'src/api/endpoints/registrationWeb';
import { GeneralPurposeVariant } from '@/constants/enums';
import Box from '@/components/BoxContainer.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useEventBus } from '@/utils/eventBus';
import { useQuasar, QForm } from 'quasar';
import Svg from '@/components/Svg.vue';
import { mymobilityWordmark } from '@/assets';
import { registerSyntheticMomentCultures } from '@/plugins/moment-cultured';
import { userLogout } from '@/services/userService';
import { getUserSession } from '@/api/endpoints/userWeb';
import { AuthPolicyEnum } from '@/constants/authPolicyEnum';
import { translateStepToRoute } from '@/utils/interfaceUtils';
import AuthenticateLayout from '@/components/layouts/AuthenticateLayout.vue';
import ProgressBar from '@/components/ProgressBar.vue';

const activationStore = useActivationStore()
const generalPurposeModalStore = useGeneralPurposeModalStore()
const router = useRouter();
const {t} = useI18n();
const sessionStore = useSessionStore();
const eventBus = useEventBus();
const $q = useQuasar();
const loginWithExisting = ref(false);
// Reactive data
const isLoading = ref(true);
const loginModel = reactive({
  password: '',
  username: '',
  countryCode: '',
  utcOffset: sessionStore.utcOffset,
});
const inputType = ref(true);
const eyeClosed = ref(false);
const validationError = ref(false);

// Computed properties
const countryList = computed(() => activationStore.supportedCountries);
const utcOffset = computed(() => sessionStore.utcOffset);

// Template Refs
const loginForm = ref<QForm | null>(null);

// Watchers
watch(() => countryList.value.selectedCountryCode, (newVal) => {
  loginModel.countryCode = newVal;
},
{ immediate: true }
);

watch(loginModel, () => {
  if (validationError.value) {
    loginForm.value?.resetValidation();
    validationError.value = false;
  }
});


// Methods
const rawRouterLink = (displayText: string, destination: string): string => {
  const destinationPath = router.getRoutes().find((d) => d.name === destination)?.path;
  let result = '';
  if (destinationPath) {
    result = `<a href="#${destinationPath}">${displayText}</a>`;
  }
  return result;
};

const onSubmitSave = async () => {
  $q.loading.show();

  try {
    const response = await handleApiRequest(() => postLoginWithExisting(loginModel), null, $q);
    if (response && response.result) {
      sessionStore.setCompletedLogin(true);

      // reload local strings
      const localizationStore = useLocalizationStore();
      await localizationStore.loadLocalizedStrings();
      registerSyntheticMomentCultures(localizationStore.locale);

      router.push({ name: 'Authenticate' });
    }
  } finally {
    $q.loading.hide();
  }

};


const toggleLoginWithExisting = async () => {
  loginWithExisting.value = !loginWithExisting.value;
};

async function onNextClick() {
  $q.loading.show()

  const step = await handleApiRequest(() => getRegistrationNextStepSkipLoginWithExting(), null, $q);
  const nextStep = translateStepToRoute(step?.result as string);
  $q.loading.hide()
  router.push({
    name: 'Authenticate',
    query: {returnRouteName: nextStep, skipStep: 'LoginWithExisting'} //skip since we are on that step, avoid loop
  });
}

// Lifecycle hooks
onMounted(async () => {

  document.querySelectorAll('html')[0].scrollTop = 0;
  (document.querySelectorAll('input:first-child')[0] as HTMLElement).focus();

  sessionStore.setIsHeaderVisible(false);

  isLoading.value = false;

});

// Expose properties for template usage
defineExpose({
  loginModel,
  inputType,
  eyeClosed,
  countryList,
  utcOffset,
  rawRouterLink,
  onSubmitSave,
});
</script>

<template>
    <q-page class="no-header login-form" padding>
        <div class="row bs-gutter justify-center" v-show="loginWithExisting">

            <!-- Box -->
            <Box :box-bkgd-color="$zb.enums.BoxBackgroundColorEnum.linearGradient"
                 :box-brdr-radius="$zb.enums.BoxBorderRadiusEnum.lg" :box-drop-shadow="$zb.enums.BoxDropShadowEnum.xl"
                 :box-padding="$zb.enums.BoxPaddingEnum.p505065" :box-width="$zb.enums.BoxWidthEnum.md" class="col-auto">

                <!-- Heading -->
                <div class="row no-wrap bs-gutter items-center login-form__heading">
                    <div class="col" />

                    <!-- mymobility Wordmark -->
                    <div class="col-auto mymo-wordmark">
                        <Svg :vector-paths="mymobilityWordmark.vectorPaths" :view-box="mymobilityWordmark.viewBox" />
                    </div>
                </div>

                <!-- Log In Form -->
                <q-form @submit.prevent="onSubmitSave" @validation-error="validationError = true" ref="loginForm">

                    <!-- Username -->
                    <div class="row justify-center mb-40">
                        <zbm-skeleton-loader :class="$zb.enums.FormElWidthEnum.qFormEl300" dark height="40px" :loading="isLoading">
                            <zbm-input-with-validation v-model="loginModel.username" dark :placeholder="t('activate_username')"
                                                       :rules="[$zb.validators.required()]" lazyRules="ondemand" />
                        </zbm-skeleton-loader>
                    </div>

                    <!-- Password -->
                    <div class="row justify-center mb-40">
                        <zbm-skeleton-loader :class="$zb.enums.FormElWidthEnum.qFormEl300" dark height="40px" :loading="isLoading">
                            <zbm-password-input-with-validation v-model="loginModel.password" dark :placeholder="t('password')"
                                                                :rules="[$zb.validators.required()]" lazyRules="ondemand" />
                        </zbm-skeleton-loader>
                    </div>

                    <!-- Country Selector -->
                    <div class="row flex-center country">

                        <!-- Label -->
                        <div class="col-auto font-size-20 text-content-grd-bg-content text-no-wrap">
                            <zbm-skeleton-loader dark height="30px" :loading="isLoading" width="107px">
                                {{ t('provider_select_country_button') }}
                            </zbm-skeleton-loader>
                        </div>
                        <div class="flex-br" />

                        <!-- Select -->
                        <zbm-skeleton-loader :class="$zb.enums.FormElWidthEnum.qFormEl300" dark height="40px" :loading="isLoading">
                            <zbm-select-with-validation v-model="loginModel.countryCode" dark
                                                        :name="t('provider_select_country_button')" option-label="text" :options="countryList.countryOptions"
                                                        option-value="value" :rules="[$zb.validators.required()]" />
                        </zbm-skeleton-loader>
                    </div>

                    <!-- Log In Button -->
                    <div class="row justify-center">
                        <zbm-skeleton-loader :class="$zb.enums.ButtonWidthEnum.w300" dark height="44px" :loading="isLoading">
                            <zbm-btn class="glow--white" :color="$zb.enums.ButtonStdColorEnum.darkTeal"
                                     :height="$zb.enums.ButtonHeightEnum.lg" :label="t('log_in')" :type="$zb.enums.ButtonTypeEnum.submit"
                                     :width="$zb.enums.ButtonWidthEnum.w300" data-testid="loginButton" />
                        </zbm-skeleton-loader>
                    </div>
                    <div class="row justify-center mt-20">
                        <zbm-skeleton-loader :class="$zb.enums.ButtonWidthEnum.w300" dark height="44px" :loading="isLoading">
                            <zbm-btn class="glow--white" :color="$zb.enums.ButtonStdColorEnum.darkTeal"
                                     :height="$zb.enums.ButtonHeightEnum.lg" :label="t('gen_back')" @click="toggleLoginWithExisting"
                                     :width="$zb.enums.ButtonWidthEnum.w300" data-testid="cancelLoginWithExisting" />
                        </zbm-skeleton-loader>
                    </div>
                </q-form>

            </Box>
        </div>
        <AuthenticateLayout withHeader v-show="!loginWithExisting">
            <template #heading>
                {{t('activation_login_existing_step_heading')}}
            </template>

            <template #default>
                <div class="row">
                    <div class="col">
                        <p>
                            {{t('activation_login_existing_step_explanation')}}
                        </p>
                        <div class="row justify-center mt-20">
                            <zbm-btn @click="toggleLoginWithExisting"
                                     :color="$zb.enums.ButtonStdColorEnum.accent"
                                     :height="$zb.enums.ButtonHeightEnum.lg"
                                     :label="t('activation_login_existing_step_login_btn')"
                                     data-testid="loginExstingButton" />
                        </div>
                        <div class="row justify-center mt-20">
                            <zbm-btn @click="onNextClick"
                                     :color="$zb.enums.ButtonStdColorEnum.accent"
                                     :height="$zb.enums.ButtonHeightEnum.lg"
                                     :label="t('activation_login_existing_step_create_btn')"
                                     data-testid="createAccountButton" />
                        </div>
                    </div>
                </div>
            </template>

            <!-- Actions/Progress -->
            <template #actions>
                <ProgressBar :backEnabled="false"
                             :loading="isLoading"
                             :nextEnabled="false"
                             :progress="75" />
            </template>

        </AuthenticateLayout>
    </q-page>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'sass:math';

.login-form { // .q-page

  // Heading
  &__heading { // .row
    margin-block-end: 118px;

    // mymobility Wordmark
    .mymo-wordmark { // [class*="col"]
      line-height: 0;
      color: map.get($co-brand-clrs, content-grd-bg-content);

      svg {
        width: 100%;
        max-width: 240px;
        height: auto;
      }
    }

    // Question Circle Button
    .question { // [class*="col"]
      text-align: end;
    }
  }

  // Form
  .q-form { // <form>
    margin-block-end: 26px;

    // Country Selector
    .country { // .row
      margin-block-end: 54px;
    }
  }

  // Forgot Username/Password
  &__forgot { // <p>
    margin-block-end: 186px;
  }

  // Activate
  &__activate { // <p>
    $login-form-activate-font-size: map.get($font-sizes, '18');

    font-size: $login-form-activate-font-size;
    line-height: math.div(26px, $login-form-activate-font-size);
  }

  @media (max-width: 479px) {

    // Heading
    &__heading { // .row

      > [class*="col"]:first-child {
        display: none;
      }

      // mymobility Wordmark
      .mymo-wordmark { // [class*="col"]
        flex: 10000 1 0;
      }

      // Question Circle Button
      .question { // [class*="col"]
        flex: 0 0 auto
      }
    }
  }

  @media (min-width: 600px) {

    // Form
    .q-form {

      // Country Selector
      .country {

        .flex-br {
          display: none;
        }

        [class*=col]:first-child {
          padding-inline-end: 10px;
        }
      }
    }
  }
}
</style>
